<template>
  <main class="news-page">
    <section class="news-hero">
      <div class="container">
        <div class="news-hero-grid">
          <div>
            <h6 class="section-title text-start text-primary text-uppercase">Ağrı Haberleri</h6>
            <h1>Ağrı, Doğubayazıt, Patnos ve ilçelerden güncel haberler</h1>
            <p>
              Ankara'da yaşayan Ağrılı hemşehrilerimiz için memleket haberlerini,
              ilçe gündemini, ekonomi, ulaşım, turizm, eğitim ve sosyal gelişmeleri tek sayfada
              takip edilebilir hale getiriyoruz.
            </p>
          </div>

          <a :href="activeGoogleNewsUrl" target="_blank" rel="noopener" class="news-source-card">
            <span>Kaynak</span>
            <strong>Google Haberler</strong>
            <small>Seçili konuya ait tüm haberleri aç</small>
          </a>
        </div>

        <div class="news-topic-tabs" role="tablist" aria-label="Haber konuları">
          <button
            v-for="topic in topics"
            :key="topic.key"
            type="button"
            :class="{ active: activeTopic === topic.key }"
            @click="selectTopic(topic.key)"
          >
            {{ topic.label }}
          </button>
        </div>
      </div>
    </section>

    <section class="news-content">
      <div class="container">
        <div v-if="isLoading" class="news-state">
          <i class="fa fa-spinner fa-spin"></i>
          <span>Haberler yükleniyor...</span>
        </div>

        <div v-else-if="errorMessage" class="news-state warning">
          <i class="fa fa-triangle-exclamation"></i>
          <div>
            <strong>Haberler şu an otomatik yüklenemedi.</strong>
            <span>{{ errorMessage }}</span>
            <a :href="activeGoogleNewsUrl" target="_blank" rel="noopener">Google Haberler'de aç</a>
          </div>
        </div>

        <div v-else class="news-grid">
          <article v-for="item in newsItems" :key="item.link" class="news-card">
            <a :href="item.link" target="_blank" rel="noopener" class="news-media" :class="{ empty: !item.thumbnail }">
              <img v-if="item.thumbnail" :src="item.thumbnail" :alt="item.title">
              <i v-else class="fa fa-newspaper"></i>
            </a>
            <div class="news-card-body">
              <div class="news-meta">
                <span>{{ item.source }}</span>
                <time>{{ item.date }}</time>
              </div>
              <h2>
                <a :href="item.link" target="_blank" rel="noopener">{{ item.title }}</a>
              </h2>
              <p>{{ item.excerpt }}</p>
              <a :href="item.link" target="_blank" rel="noopener" class="news-read-link">
                Haberi Oku
                <i class="fa fa-arrow-right"></i>
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'

const topics = [
  { key: 'agri', label: 'Ağrı', query: 'Ağrı haberleri' },
  { key: 'dogubayazit', label: 'Doğubayazıt', query: 'Doğubayazıt haberleri' },
  { key: 'patnos', label: 'Patnos', query: 'Patnos haberleri' },
  { key: 'diyadin', label: 'Diyadin', query: 'Diyadin haberleri' },
  { key: 'eleskirt', label: 'Eleşkirt', query: 'Eleşkirt haberleri' },
  { key: 'tutak', label: 'Tutak', query: 'Tutak haberleri' },
  { key: 'taslicay', label: 'Taşlıçay', query: 'Taşlıçay haberleri' },
  { key: 'hamur', label: 'Hamur', query: 'Hamur Ağrı haberleri' },
  { key: 'ankara', label: 'Ankara Ağrılılar', query: 'Ankara Ağrılılar haberleri' }
]

const activeTopic = ref('agri')
const isLoading = ref(false)
const errorMessage = ref('')
const newsItems = ref([])

const activeTopicItem = computed(() => topics.find((topic) => topic.key === activeTopic.value) || topics[0])
const activeGoogleNewsUrl = computed(() => `https://news.google.com/search?q=${encodeURIComponent(activeTopicItem.value.query)}&hl=tr&gl=TR&ceid=TR%3Atr`)

const buildRssUrl = (query) => `https://news.google.com/rss/search?q=${encodeURIComponent(query)}&hl=tr&gl=TR&ceid=TR:tr`
const buildApiUrl = (query) => `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(buildRssUrl(query))}`

const stripHtml = (value) => String(value || '')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&')
  .replace(/\s+/g, ' ')
  .trim()

const formatDate = (value) => {
  if (!value) return ''
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(new Date(value))
}

const mapItem = (item) => ({
  title: stripHtml(item.title),
  link: item.link,
  source: item.author || item.source || 'Haber kaynağı',
  date: formatDate(item.pubDate),
  thumbnail: item.thumbnail || item.enclosure?.link || '',
  excerpt: stripHtml(item.description).slice(0, 180)
})

const loadNews = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const response = await fetch(buildApiUrl(activeTopicItem.value.query))
    if (!response.ok) throw new Error('Haber servisi cevap vermedi.')

    const data = await response.json()
    if (data.status !== 'ok') throw new Error(data.message || 'Haber akışı okunamadı.')

    newsItems.value = (data.items || [])
      .map(mapItem)
      .filter((item) => item.title && item.link)
      .slice(0, 12)

    if (!newsItems.value.length) {
      throw new Error('Bu konu için haber bulunamadı.')
    }
  } catch (error) {
    newsItems.value = []
    errorMessage.value = error?.message || 'Haberler yüklenirken beklenmeyen bir sorun oluştu.'
  } finally {
    isLoading.value = false
  }
}

const selectTopic = (key) => {
  activeTopic.value = key
  loadNews()
}

onMounted(loadNews)
</script>

<style scoped>
.news-page {
  background: linear-gradient(180deg, #fff 0%, #f7fafc 46%, #fff 100%);
  min-height: 100vh;
}

.news-hero {
  padding: 56px 0 26px;
}

.news-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 30px;
  align-items: end;
}

.news-hero h1 {
  color: var(--dark);
  font-size: clamp(34px, 5vw, 58px);
  line-height: 1.08;
  max-width: 980px;
  margin: 0 0 18px;
}

.news-hero p {
  color: #5b6475;
  font-size: 18px;
  line-height: 1.75;
  max-width: 900px;
  margin: 0;
}

.news-source-card {
  display: grid;
  gap: 8px;
  color: #fff;
  background: var(--dark);
  border-left: 5px solid var(--primary);
  border-radius: 8px;
  padding: 22px;
  box-shadow: 0 18px 38px rgba(15, 23, 43, .14);
}

.news-source-card span,
.news-source-card small {
  color: rgba(255, 255, 255, .72);
  font-weight: 800;
}

.news-source-card strong {
  color: #fff;
  font-size: 24px;
}

.news-topic-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
}

.news-topic-tabs button {
  color: var(--dark);
  background: #fff;
  border: 1px solid rgba(15, 23, 43, .1);
  border-left: 3px solid transparent;
  border-radius: 999px;
  font-weight: 900;
  padding: 9px 13px;
}

.news-topic-tabs button.active,
.news-topic-tabs button:hover {
  color: #7a4c00;
  background: #fff4df;
  border-color: #ffe2aa;
  border-left-color: var(--primary);
}

.news-content {
  padding: 20px 0 76px;
}

.news-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.news-card {
  display: grid;
  grid-template-rows: 180px minmax(0, 1fr);
  background: #fff;
  border: 1px solid rgba(15, 23, 43, .08);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(15, 23, 43, .07);
}

.news-media {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  overflow: hidden;
}

.news-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-media.empty {
  color: var(--primary);
  font-size: 42px;
}

.news-card-body {
  display: grid;
  gap: 11px;
  padding: 18px;
}

.news-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: #687386;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.news-card h2 {
  font-size: 21px;
  line-height: 1.25;
  margin: 0;
}

.news-card h2 a {
  color: var(--dark);
}

.news-card p {
  color: #5f6979;
  line-height: 1.62;
  margin: 0;
}

.news-read-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--primary);
  font-weight: 900;
}

.news-state {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid rgba(15, 23, 43, .08);
  border-radius: 8px;
  padding: 22px;
  color: var(--dark);
  font-weight: 900;
}

.news-state.warning {
  align-items: flex-start;
  border-left: 4px solid var(--primary);
}

.news-state.warning div {
  display: grid;
  gap: 6px;
}

@media (max-width: 991.98px) {
  .news-hero-grid,
  .news-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 575.98px) {
  .news-page {
    padding-bottom: 86px;
  }

  .news-hero {
    padding-top: 38px;
  }

  .news-hero h1 {
    font-size: 32px;
  }

  .news-hero p {
    font-size: 16px;
  }

  .news-card {
    grid-template-rows: 160px minmax(0, 1fr);
  }
}
</style>
